import { useState } from 'react'
import { Head } from '@inertiajs/react'
import DashboardLayout from '@/components/DashboardLayout'
import { Badge } from '@/components/ui/badge'
import { PlayCircle, Clock, GraduationCap, MapPin, ListChecks, Lightbulb } from 'lucide-react'

interface TutorialStep {
  title: string
  body: string
}

interface Tutorial {
  id: number
  title: string
  subtitle: string
  description: string
  url: string | null
  youtube_id: string | null
  published: boolean
  menu: string[]
  steps: TutorialStep[]
  tips: string[]
}

interface TutorialsProps {
  tutorials: Tutorial[]
}

function thumbnailUrl(tutorial: Tutorial): string | null {
  if (!tutorial.youtube_id) return null
  return `https://img.youtube.com/vi/${tutorial.youtube_id}/hqdefault.jpg`
}

export default function TutorialsIndex({ tutorials }: TutorialsProps) {
  const firstPublished = tutorials.find((t) => t.published) ?? tutorials[0] ?? null
  const [selected, setSelected] = useState<Tutorial | null>(firstPublished)

  return (
    <DashboardLayout>
      <Head title="Tutoriais" />
      <div className="p-4 md:p-8 space-y-6">
        {/* Cabeçalho */}
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <GraduationCap className="h-6 w-6" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Tutoriais</h1>
            <p className="text-sm text-muted-foreground">
              Vídeos de treinamento e guias passo a passo para dominar o painel da Bewave
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Coluna principal: player + conteúdo */}
          <div className="lg:col-span-2 space-y-6">
            {/* Player */}
            <div className="aspect-video w-full overflow-hidden rounded-2xl border bg-black shadow-sm">
              {selected?.published && selected.youtube_id ? (
                <iframe
                  key={selected.youtube_id}
                  className="h-full w-full"
                  src={`https://www.youtube.com/embed/${selected.youtube_id}`}
                  title={selected.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : (
                <div className="flex h-full w-full flex-col items-center justify-center gap-3 bg-muted text-muted-foreground">
                  <Clock className="h-10 w-10" />
                  <p className="text-sm font-medium">Vídeo em breve — o guia abaixo já está disponível</p>
                </div>
              )}
            </div>

            {selected && (
              <>
                {/* Título + descrição */}
                <div className="space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="text-xl font-semibold tracking-tight">{selected.title}</h2>
                    {selected.published ? (
                      <Badge variant="secondary" className="bg-primary/10 text-primary hover:bg-primary/10">
                        {selected.subtitle}
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-muted-foreground">
                        Em breve
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm leading-relaxed text-muted-foreground">
                    {selected.description}
                  </p>
                </div>

                {/* Onde encontrar */}
                {selected.menu?.length > 0 && (
                  <div className="rounded-xl border bg-card p-4">
                    <div className="mb-2 flex items-center gap-2 text-sm font-semibold">
                      <MapPin className="h-4 w-4 text-primary" />
                      Onde encontrar no painel
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      {selected.menu.map((item) => (
                        <Badge key={item} variant="outline" className="font-normal">
                          Menu → {item}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Passo a passo */}
                {selected.steps?.length > 0 && (
                  <div className="rounded-xl border bg-card p-4 md:p-5">
                    <div className="mb-4 flex items-center gap-2 text-sm font-semibold">
                      <ListChecks className="h-4 w-4 text-primary" />
                      Passo a passo
                    </div>
                    <ol className="space-y-4">
                      {selected.steps.map((step, index) => (
                        <li key={index} className="flex gap-3">
                          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                            {index + 1}
                          </span>
                          <div className="space-y-0.5">
                            <p className="text-sm font-medium leading-snug">{step.title}</p>
                            <p className="text-sm leading-relaxed text-muted-foreground">{step.body}</p>
                          </div>
                        </li>
                      ))}
                    </ol>
                  </div>
                )}

                {/* Dicas */}
                {selected.tips?.length > 0 && (
                  <div className="rounded-xl border border-primary/20 bg-primary/5 p-4 md:p-5">
                    <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-primary">
                      <Lightbulb className="h-4 w-4" />
                      Dicas importantes
                    </div>
                    <ul className="space-y-2">
                      {selected.tips.map((tip, index) => (
                        <li key={index} className="flex gap-2 text-sm leading-relaxed">
                          <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                          <span>{tip}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Lista de tutoriais */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                Conteúdos
              </h3>
              <span className="text-xs text-muted-foreground">
                {tutorials.length} {tutorials.length === 1 ? 'guia' : 'guias'}
              </span>
            </div>

            <div className="space-y-3">
              {tutorials.map((tutorial) => {
                const isActive = selected?.id === tutorial.id
                const thumb = thumbnailUrl(tutorial)

                return (
                  <button
                    key={tutorial.id}
                    type="button"
                    onClick={() => setSelected(tutorial)}
                    className={`group flex w-full gap-3 rounded-xl border p-2.5 text-left transition-colors ${
                      isActive
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-primary/40 hover:bg-accent'
                    } cursor-pointer`}
                  >
                    {/* Thumbnail */}
                    <div className="relative aspect-video w-28 shrink-0 overflow-hidden rounded-lg bg-muted">
                      {thumb ? (
                        <img
                          src={thumb}
                          alt={tutorial.title}
                          className="h-full w-full object-cover"
                          loading="lazy"
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center text-muted-foreground">
                          <Clock className="h-5 w-5" />
                        </div>
                      )}
                      {tutorial.published && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 transition-opacity group-hover:opacity-100">
                          <PlayCircle className="h-8 w-8 text-white" />
                        </div>
                      )}
                    </div>

                    {/* Texto */}
                    <div className="flex min-w-0 flex-1 flex-col gap-1 py-0.5">
                      <p className={`text-sm font-medium leading-snug line-clamp-2 ${isActive ? 'text-primary' : ''}`}>
                        {tutorial.title}
                      </p>
                      {tutorial.published ? (
                        <p className="text-xs text-muted-foreground line-clamp-2">
                          {tutorial.subtitle}
                        </p>
                      ) : (
                        <Badge variant="outline" className="w-fit text-[10px] text-muted-foreground">
                          Em breve
                        </Badge>
                      )}
                    </div>
                  </button>
                )
              })}
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  )
}
